/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { defaultStoreUS } from '@/constants/locale';
import { useState, type ReactNode } from 'react';
import { useSignInFlowType } from '../SignInFlowTypeContext';
import { Actionable } from '@az/starc-ui';
import dynamic from 'next/dynamic';
import ProgressModal from '../AZCustomComponent/ProgressModal';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useAppData } from '@/hooks/useAppData';

const LazySignInModalV2 = dynamic(() => import('@/components/SignInModalV2'), {
  loading: () => <ProgressModal noScroll />,
});

const LazySearchStoreModal = dynamic(
  () =>
    import('@/components/Store/StoreModal/SearchStoreModal').then((mod) => mod.SearchStoreModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyMyStoreModal = dynamic(
  () => import('@/components/Store/StoreModal/MyStoreModal').then((mod) => mod.MyStoreModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

type ModalLinkProps = {
  modalId: string;
  className?: string;
  'aria-label'?: string;
  children: ReactNode;
};

export const ModalLink = ({
  modalId,
  className,
  'aria-label': ariaLabel,
  children,
}: ModalLinkProps) => {
  const [storeModalOpen, setStoreModalOpen] = useState(false);

  const { data: storeData } = useStoreDetailsData();
  const storeNumber = storeData?.storeNumber ?? defaultStoreUS;

  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const { setFlowTypeFn } = useSignInFlowType();
  const { appData } = useAppData();
  const isLoggedIn = appData.userAuthenticated;

  const handleModalOpen = (id: string) => {
    switch (id) {
      case '[search_store_modal]':
        setStoreModalOpen(true);
        break;
      case '[sign_in_modal]':
      case '[create_account_modal]':
        if (isLoggedIn) {
          break;
        }

        setFlowTypeFn('login');
        setSignInModalOpen(true);
        break;
      case '[cookie_preference_center_modal]':
        window.OneTrust?.ToggleInfoDisplay();
        break;
    }
  };

  const isSignInModal = modalId === '[sign_in_modal]' || modalId === '[create_account_modal]';
  const StoreModal = storeNumber !== defaultStoreUS ? LazyMyStoreModal : LazySearchStoreModal;

  return (
    <>
      <Actionable
        className={className}
        attributes={{ 'aria-label': ariaLabel }}
        onClick={() => handleModalOpen(modalId)}
      >
        {children}
      </Actionable>
      {isSignInModal && (
        <LazySignInModalV2 isOpen={signInModalOpen} onClose={() => setSignInModalOpen(false)} />
      )}
      {storeModalOpen && <StoreModal closeStoreModal={() => setStoreModalOpen(false)} />}
    </>
  );
};
